import { useRef, useState } from 'react';
import { Area, AreaChart, CartesianGrid, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import MonteCarloWorker from '@/engine/monteCarlo.worker.ts?worker';
import type { MonteCarloRequest, MonteCarloResponse } from '@/engine/monteCarlo.worker';
import { runMonteCarlo } from '@/engine/monteCarlo';
import { monteCarloConfigSchema } from '@/schema';
import { useStore } from '@/state/store';
import { fmtCurrency, fmtCurrencyCompact, fmtRatioPct } from '@/lib/format';
import { COLORS, downsample, useIsDark, yearAxisProps, TOOLTIP_PROPS } from './charts/chartUtils';
import { HowToRead } from './HowToRead';
import { CHART_HELP } from '@/content/chartHelp';
import { TOOLTIPS } from '@/content/tooltips';
import { InfoTooltip } from './InfoTooltip';

/** Monte Carlo panel — runs in a Web Worker with a progress bar (§6.18, §10.7). */
export function MonteCarloPanel() {
  const inputs = useStore((s) => s.inputs);
  const result = useStore((s) => s.mcResult);
  const setMcResult = useStore((s) => s.setMcResult);
  const dark = useIsDark();
  const [runs, setRuns] = useState(1000);
  const [vol, setVol] = useState(15);
  const [rho, setRho] = useState(0.3);
  const [stochasticBaseRate, setStochasticBaseRate] = useState(false);
  const [seed, setSeed] = useState(12345);
  const [progress, setProgress] = useState<number | null>(null);
  const workerRef = useRef<Worker | null>(null);

  const run = () => {
    const config = monteCarloConfigSchema.parse({ runs, volatilityPct: vol, crossAssetCorrelation: rho, stochasticBaseRate, seed });
    setProgress(0);
    try {
      const worker = new MonteCarloWorker();
      workerRef.current = worker;
      worker.onmessage = (e: MessageEvent<MonteCarloResponse>) => {
        if (e.data.type === 'progress') setProgress(e.data.fraction);
        else {
          setMcResult(e.data.result);
          setProgress(null);
          worker.terminate();
        }
      };
      const req: MonteCarloRequest = { inputs, config };
      worker.postMessage(req);
    } catch {
      // Fallback (e.g. no Worker support): run synchronously.
      setMcResult(runMonteCarlo(inputs, config));
      setProgress(null);
    }
  };

  const fan = result ? downsample(result.netWorthPercentiles).map((p) => ({
    year: +(p.monthIndex / 12).toFixed(2),
    p5: p.p5,
    band25: p.p25 - p.p5,
    band50: p.p50 - p.p25,
    band75: p.p75 - p.p50,
    band95: p.p95 - p.p75,
    p50: p.p50,
  })) : [];

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <h3 className="mb-2 text-sm font-semibold">Monte Carlo</h3>
      <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-3">
        <label className="flex flex-col">Runs<input type="number" className="rounded border border-slate-300 px-1 py-0.5 dark:border-slate-600 dark:bg-slate-900" value={runs} onChange={(e) => setRuns(Number(e.target.value))} /></label>
        <label className="flex flex-col">Volatility %<input type="number" className="rounded border border-slate-300 px-1 py-0.5 dark:border-slate-600 dark:bg-slate-900" value={vol} onChange={(e) => setVol(Number(e.target.value))} /></label>
        <label className="flex flex-col">Seed<input type="number" className="rounded border border-slate-300 px-1 py-0.5 dark:border-slate-600 dark:bg-slate-900" value={seed} onChange={(e) => setSeed(Number(e.target.value))} /></label>
        <label className="col-span-2 flex flex-col">
          <span className="flex items-center">Correlation ρ {rho.toFixed(2)}<InfoTooltip text={TOOLTIPS.crossAssetCorrelation} label="Cross-asset correlation" /></span>
          <input type="range" min={0} max={1} step={0.05} value={rho} onChange={(e) => setRho(Number(e.target.value))} className="accent-sky-600" />
        </label>
        <label className="flex items-center gap-1"><input type="checkbox" className="accent-sky-600" checked={stochasticBaseRate} onChange={(e) => setStochasticBaseRate(e.target.checked)} />Stochastic SOFR</label>
      </div>
      <button type="button" className="mt-2 rounded bg-sky-600 px-3 py-1 text-sm text-white disabled:opacity-50" disabled={progress !== null} onClick={run}>
        {progress !== null ? `Running… ${Math.round(progress * 100)}%` : 'Run simulation'}
      </button>
      {progress !== null && (
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded bg-slate-200 dark:bg-slate-700">
          <div className="h-full bg-sky-600 transition-all" style={{ width: `${progress * 100}%` }} />
        </div>
      )}

      {result && (
        <div className="mt-3">
          <p className="text-sm">
            Maintenance-call probability:{' '}
            <strong className={result.maintenanceCallProbability > 0.1 ? 'text-danger-surface' : 'text-safe-surface'}>{fmtRatioPct(result.maintenanceCallProbability)}</strong>
          </p>
          <div className="mt-2 h-64">
            <ResponsiveContainer>
              <AreaChart data={fan} margin={{ top: 5, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid stroke={dark ? '#334155' : '#e2e8f0'} strokeDasharray="3 3" />
                <XAxis stroke={dark ? '#94a3b8' : '#64748b'} fontSize={11} {...yearAxisProps(fan)} />
                <YAxis stroke={dark ? '#94a3b8' : '#64748b'} fontSize={11} tickFormatter={fmtCurrencyCompact} width={56} />
                <Tooltip {...TOOLTIP_PROPS} formatter={(v: number | string) => fmtCurrency(Number(v))} />
                <Legend />
                <Area dataKey="p5" stackId="b" stroke="none" fill="transparent" name="p5" />
                <Area dataKey="band25" stackId="b" stroke="none" fill={COLORS.netWorth} fillOpacity={0.12} name="5–25%" />
                <Area dataKey="band50" stackId="b" stroke="none" fill={COLORS.netWorth} fillOpacity={0.22} name="25–50%" />
                <Area dataKey="band75" stackId="b" stroke="none" fill={COLORS.netWorth} fillOpacity={0.22} name="50–75%" />
                <Area dataKey="band95" stackId="b" stroke="none" fill={COLORS.netWorth} fillOpacity={0.12} name="75–95%" />
                <Line dataKey="p50" stroke={COLORS.netWorth} strokeWidth={2} dot={false} name="Median" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <table className="mt-2 w-full text-left text-xs">
            <thead className="text-slate-500"><tr><th>Terminal</th><th>p5</th><th>p25</th><th>p50</th><th>p75</th><th>p95</th></tr></thead>
            <tbody>
              {(['netWorth', 'loanBalance', 'heirsValue'] as const).map((k) => (
                <tr key={k} className="border-t border-slate-100 dark:border-slate-700">
                  <td className="py-0.5 capitalize">{k.replace(/([A-Z])/g, ' $1')}</td>
                  {(['p5', 'p25', 'p50', 'p75', 'p95'] as const).map((p) => <td key={p} className="tabular-nums">{fmtCurrencyCompact(result.terminal[k][p])}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
          <HowToRead text={CHART_HELP.monteCarlo} />
        </div>
      )}
    </div>
  );
}
